import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { teamApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import type { Team, TeamImportData } from '@/types';

const statusColors: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  revision_requested: 'info',
};

const SORT_OPTIONS = [
  { label: 'Newest first', sort_by: 'created_at', sort_order: 'desc' },
  { label: 'Oldest first', sort_by: 'created_at', sort_order: 'asc' },
  { label: 'Name A\u2013Z', sort_by: 'name', sort_order: 'asc' },
  { label: 'Name Z\u2013A', sort_by: 'name', sort_order: 'desc' },
];

const DEFAULT_FILTERS = {
  approval_status: '',
  jira_project_key: '',
  timezone: '',
  description: '',
};

function countActiveFilters(filters: typeof DEFAULT_FILTERS): number {
  return Object.values(filters).filter((v) => v !== '').length;
}

export default function TeamsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Pagination + search + sort state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(24);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortIndex, setSortIndex] = useState(0);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Filter panel state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
    google_space_id: '',
    jira_project_key: '',
    timezone: 'UTC',
  });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importTeamId, setImportTeamId] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTeam, setMenuTeam] = useState<Team | null>(null);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 300);
  };

  const updateFilter = (key: keyof typeof DEFAULT_FILTERS, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearAllFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  const sort = SORT_OPTIONS[sortIndex];
  const activeFilterCount = countActiveFilters(filters);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'teams',
      debouncedSearch,
      sort.sort_by,
      sort.sort_order,
      page,
      rowsPerPage,
      filters,
    ],
    queryFn: () =>
      teamApi
        .list({
          search: debouncedSearch || undefined,
          approval_status: filters.approval_status || undefined,
          is_active: true,
          sort_by: sort.sort_by,
          sort_order: sort.sort_order,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          jira_project_key: filters.jira_project_key || undefined,
          timezone: filters.timezone || undefined,
          description: filters.description || undefined,
        })
        .then((res) => res.data),
    placeholderData: keepPreviousData,
  });

  const teams = data?.items || [];
  const totalTeams = data?.total || 0;

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    team: Team
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuTeam(team);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuTeam(null);
  };

  const createMutation = useMutation({
    mutationFn: (data: Partial<Team>) => teamApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setCreateDialogOpen(false);
      setNewTeam({
        name: '',
        description: '',
        google_space_id: '',
        jira_project_key: '',
        timezone: 'UTC',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (teamId: string) => teamApi.delete(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setDeleteDialogOpen(false);
      setTeamToDelete(null);
    },
    onError: (error: Error) => {
      console.error('Delete failed:', error);
      alert(`Failed to delete team: ${error.message}`);
    },
  });

  const importMutation = useMutation({
    mutationFn: ({ teamId, data }: { teamId: string; data: TeamImportData }) =>
      teamApi.importTeam(teamId, data).then((res) => res.data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setImportResult(`Import complete: ${result.inserted} inserted, ${result.skipped} skipped, ${result.failed} failed`);
      if (result.errors.length > 0) {
        setImportResult((prev) => `${prev}\nErrors: ${result.errors.join(', ')}`);
      }
    },
    onError: (error: Error) => {
      setImportResult(`Import failed: ${error.message}`);
    },
  });

  const handleExportTeam = async (team: Team) => {
    try {
      const response = await teamApi.exportTeam(team.id);
      const data = response.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `team-${team.name.toLowerCase().replace(/\s+/g, '-')}-backup.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export team configuration');
    }
  };

  const handleImportClick = (teamId: string) => {
    setImportTeamId(teamId);
    setImportResult(null);
    setImportDialogOpen(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !importTeamId) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importData = JSON.parse(content);

        // Extract importable data from the export format
        const teamImportData: TeamImportData = {
          members: importData.members?.map((m: Record<string, unknown>) => ({
            google_user_id: m.google_user_id,
            email: m.email,
            display_name: m.display_name,
            role: m.role,
          })),
          documentation: importData.documentation?.map((d: Record<string, unknown>) => ({
            url: d.url,
            title: d.title,
            doc_type: d.doc_type,
            description: d.description,
          })),
          repositories: importData.repositories?.map((r: Record<string, unknown>) => ({
            url: r.url,
            name: r.name,
            platform: r.platform,
            description: r.description,
            default_branch: r.default_branch,
            language: r.language,
            is_primary: r.is_primary,
          })),
          experts: importData.experts?.map((e: Record<string, unknown>) => ({
            expert_name: e.expert_name,
            expert_email: e.expert_email,
            expert_tag: e.expert_tag,
            expertise: e.expertise,
            keywords: e.keywords,
            knowledge_level: e.knowledge_level,
            availability: e.availability,
          })),
          responsibilities: importData.responsibilities?.map((r: Record<string, unknown>) => ({
            component: r.component,
            responsible_team: r.responsible_team,
            contact_space_url: r.contact_space_url,
            escalation_email: r.escalation_email,
            keywords: r.keywords,
          })),
        };

        importMutation.mutate({ teamId: importTeamId, data: teamImportData });
      } catch {
        setImportResult('Failed to parse import file. Please ensure it is a valid JSON export.');
      }
    };
    reader.readAsText(file);

    // Reset file input
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleCreateTeam = () => {
    createMutation.mutate(newTeam);
  };

  const handleDeleteClick = (team: Team) => {
    setTeamToDelete(team);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (teamToDelete) {
      deleteMutation.mutate(teamToDelete.id);
    }
  };

  if (isLoading && !data) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={100} height={40} />
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Skeleton variant="rectangular" height={40} sx={{ flex: 1, borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
          </Stack>
        </Box>
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={i}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load teams</Alert>;
  }

  return (
    <Box>
      {/* Header: title + new team button */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Teams</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ px: 3 }}
        >
          New Team
        </Button>
      </Box>

      {/* Search + filters button + sort toolbar */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
        <TextField
          sx={{ flex: 1 }}
          placeholder="Search teams by name or description..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          size="small"
        />
        <Button
          variant={filtersOpen ? 'contained' : 'outlined'}
          startIcon={
            <Badge badgeContent={activeFilterCount} color="primary" max={9}>
              <FilterListIcon />
            </Badge>
          }
          onClick={() => setFiltersOpen(!filtersOpen)}
          sx={{ minWidth: 120 }}
          size="small"
        >
          Filters
        </Button>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Sort</InputLabel>
          <Select
            value={sortIndex}
            label="Sort"
            onChange={(e) => { setSortIndex(e.target.value as number); setPage(0); }}
          >
            {SORT_OPTIONS.map((opt, i) => (
              <MenuItem key={i} value={i}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Collapsible Filter Panel */}
      <Collapse in={filtersOpen}>
        <Card variant="outlined" sx={{ mb: 2, mt: 1 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Filters</Typography>
              <IconButton size="small" onClick={() => setFiltersOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              {/* Row 1 */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Approval Status</InputLabel>
                  <Select
                    value={filters.approval_status}
                    label="Approval Status"
                    onChange={(e) => updateFilter('approval_status', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="revision_requested">Revision Requested</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="JIRA Project Key"
                  placeholder="e.g. PLAT"
                  value={filters.jira_project_key}
                  onChange={(e) => updateFilter('jira_project_key', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Timezone"
                  placeholder="e.g. Europe/Berlin"
                  value={filters.timezone}
                  onChange={(e) => updateFilter('timezone', e.target.value)}
                />
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description contains"
                  value={filters.description}
                  onChange={(e) => updateFilter('description', e.target.value)}
                />
              </Grid>
            </Grid>

            {/* Footer */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Button
                size="small"
                onClick={clearAllFilters}
                disabled={activeFilterCount === 0}
              >
                Clear all
              </Button>
              {activeFilterCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Collapse>

      {/* Results summary */}
      {totalTeams > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Showing {page * rowsPerPage + 1}&ndash;{Math.min((page + 1) * rowsPerPage, totalTeams)} of {totalTeams} team{totalTeams !== 1 ? 's' : ''}
        </Typography>
      )}

      {totalTeams === 0 && !debouncedSearch && activeFilterCount === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No teams yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first team to get started
            </Typography>
            <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
              Create Team
            </Button>
          </CardContent>
        </Card>
      ) : totalTeams === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No teams match your filters
            </Typography>
            <Button
              variant="text"
              onClick={() => { setSearchQuery(''); setDebouncedSearch(''); clearAllFilters(); }}
            >
              Clear filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={2}>
            {teams.map((team) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={team.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    },
                  }}
                  onClick={() => navigate(`/teams/${team.id}`)}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 1.5 }, flex: 1 }}>
                    {/* Row 1: Team name + 3-dot menu */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ fontWeight: 600, flex: 1, mr: 1 }}
                      >
                        {team.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, team)}
                        sx={{ p: 0.5 }}
                      >
                        <MoreVertIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>

                    {/* Row 2: Status chip + JIRA badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                      <Chip
                        label={team.approval_status}
                        color={statusColors[team.approval_status] || 'default'}
                        size="small"
                        sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                      />
                      {team.jira_project_key && (
                        <Chip
                          label={team.jira_project_key}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                        />
                      )}
                    </Box>

                    {/* Row 3: 1-line truncated description */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                      }}
                    >
                      {team.description || 'No description'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={totalTeams}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[12, 24, 48]}
            sx={{ mt: 2 }}
          />
        </>
      )}

      {/* 3-dot action menu (shared across all cards) */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { if (menuTeam) navigate(`/teams/${menuTeam.id}`); handleMenuClose(); }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (menuTeam) navigate(`/teams/${menuTeam.id}/onboarding`); handleMenuClose(); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Onboarding</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (menuTeam) handleExportTeam(menuTeam); handleMenuClose(); }}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (menuTeam) handleImportClick(menuTeam.id); handleMenuClose(); }}>
          <ListItemIcon><UploadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Import</ListItemText>
        </MenuItem>
        {user?.isAdmin && (
          <MenuItem
            onClick={() => { if (menuTeam) handleDeleteClick(menuTeam); handleMenuClose(); }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create Team Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Team</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Team Name"
              value={newTeam.name}
              onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={newTeam.description}
              onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              label="Google Space ID"
              value={newTeam.google_space_id}
              onChange={(e) => setNewTeam({ ...newTeam, google_space_id: e.target.value })}
              required
              fullWidth
              helperText="The unique identifier for your Google Chat space"
            />
            <TextField
              label="JIRA Project Key"
              value={newTeam.jira_project_key}
              onChange={(e) => setNewTeam({ ...newTeam, jira_project_key: e.target.value })}
              fullWidth
              helperText="Optional: e.g., PROJ"
            />
            <TextField
              label="Timezone"
              value={newTeam.timezone}
              onChange={(e) => setNewTeam({ ...newTeam, timezone: e.target.value })}
              fullWidth
              helperText="e.g., UTC, Europe/Berlin"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateTeam}
            disabled={!newTeam.name || !newTeam.google_space_id || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the team "{teamToDelete?.name}"? This action will deactivate the team.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Configuration Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Team Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Select a JSON backup file to restore team configuration. This will import members, documentation, repositories, experts, and responsibilities.
            </Typography>
            <input
              type="file"
              accept=".json"
              ref={(el) => { fileInputRef.current = el; }}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              disabled={importMutation.isPending}
              startIcon={<UploadIcon />}
            >
              {importMutation.isPending ? 'Importing...' : 'Select File'}
            </Button>
            {importResult && (
              <Alert severity={importResult.includes('failed') ? 'error' : 'success'} sx={{ whiteSpace: 'pre-wrap' }}>
                {importResult}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
